import { components } from "@/api-schema";
import { RelocationOrderTransportMethod, RelocationOrderTransportReason } from "@/api-schema-enums";
import { AddressFormValues } from "@/components/address-form-fields/address-form-schema";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

export enum RelocationOrderType {
  BranchToBranch = "Branch_To_Branch",
  BranchToExternalAddress = "Branch_To_External_Address",
  ExternalAddressToBranch = "External_Address_To_Branch",
}

type Vehicle = components["schemas"]["Vehicle"];
type Branch = components["schemas"]["Branch"];

interface RelocationOrderInitState {
  hasHydrated: boolean;
  setHasHydrated: (hasHydrated: boolean) => void;

  // Step 1
  vehicle: Vehicle | null;
  setVehicle: (vehicle: Vehicle) => void;

  // Step 2
  transportMethod: RelocationOrderTransportMethod;
  setTransportMethod: (method: RelocationOrderTransportMethod) => void;
  transportCompanyUuid: string | null;
  setTransportCompanyUuid: (uuid: string | null) => void;
  relocationOrderType: RelocationOrderType;
  setRelocationOrderType: (type: RelocationOrderType) => void;

  // Step 3 (only for ExternalToBranch)
  originExternalAddress: AddressFormValues | null;
  setOriginExternalAddress: (address: AddressFormValues) => void;

  // Step 4-1 (for BranchToBranch / ExternalToBranch)
  destinationBranch: Branch | null;
  setDestinationBranch: (branch: Branch) => void;

  // Step 4-2 (only for BranchToExternal)
  destinationExternalAddress: AddressFormValues | null;
  setDestinationExternalAddress: (address: AddressFormValues) => void;

  // Step 5
  earliestPickupDate: Date | null;
  setEarliestPickupDate: (date: Date) => void;
  transportReason: RelocationOrderTransportReason | null;
  setTransportReason: (reason: RelocationOrderTransportReason) => void;

  reset: () => void;
}

type PersistedRelocationOrderInitState = Omit<
  RelocationOrderInitState,
  | "hasHydrated"
  | "setHasHydrated"
  | "setVehicle"
  | "setTransportMethod"
  | "setTransportCompanyUuid"
  | "setRelocationOrderType"
  | "setOriginExternalAddress"
  | "setDestinationBranch"
  | "setDestinationExternalAddress"
  | "setEarliestPickupDate"
  | "setTransportReason"
  | "reset"
>;

type SerializedPersistedRelocationOrderInitState = Omit<PersistedRelocationOrderInitState, "earliestPickupDate"> & {
  earliestPickupDate: string | null;
};

const initialState = {
  vehicle: null,
  transportMethod: RelocationOrderTransportMethod.TransportCompany,
  transportCompanyUuid: null,
  relocationOrderType: RelocationOrderType.BranchToBranch,
  originExternalAddress: null,
  destinationBranch: null,
  destinationExternalAddress: null,
  earliestPickupDate: null,
  transportReason: RelocationOrderTransportReason.ChangeOfLocation,
} satisfies PersistedRelocationOrderInitState;

const downstreamStepState = {
  originExternalAddress: initialState.originExternalAddress,
  destinationBranch: initialState.destinationBranch,
  destinationExternalAddress: initialState.destinationExternalAddress,
  earliestPickupDate: initialState.earliestPickupDate,
  transportReason: initialState.transportReason,
} satisfies Partial<PersistedRelocationOrderInitState>;

function serializeEarliestPickupDate(date: Date | null): string | null {
  return date ? date.toISOString() : null;
}

function deserializeEarliestPickupDate(date: string | null | undefined): Date | null {
  if (!date) {
    return null;
  }

  const deserializedDate = new Date(date);

  return Number.isNaN(deserializedDate.getTime()) ? null : deserializedDate;
}

export const useRelocationOrderInitStore = create<RelocationOrderInitState>()(
  devtools(
    persist(
      (set) => ({
        hasHydrated: false,
        ...initialState,

        setHasHydrated: (hasHydrated) => set({ hasHydrated }, false, "setHasHydrated"),
        setVehicle: (vehicle) => set({ ...initialState, vehicle, hasHydrated: true }, false, "setVehicle"),
        setTransportMethod: (transportMethod) =>
          set({ transportMethod, transportCompanyUuid: null }, false, "setTransportMethod"),
        setTransportCompanyUuid: (transportCompanyUuid) =>
          set({ transportCompanyUuid }, false, "setTransportCompanyUuid"),
        setRelocationOrderType: (relocationOrderType) =>
          set({ relocationOrderType, ...downstreamStepState }, false, "setRelocationOrderType"),
        setOriginExternalAddress: (originExternalAddress) =>
          set({ originExternalAddress }, false, "setOriginExternalAddress"),
        setDestinationBranch: (destinationBranch) => set({ destinationBranch }, false, "setDestinationBranch"),
        setDestinationExternalAddress: (destinationExternalAddress) =>
          set({ destinationExternalAddress }, false, "setDestinationExternalAddress"),
        setEarliestPickupDate: (earliestPickupDate) => set({ earliestPickupDate }, false, "setEarliestPickupDate"),
        setTransportReason: (transportReason) => set({ transportReason }, false, "setTransportReason"),

        reset: () => set({ ...initialState, hasHydrated: true }, false, "reset"),
      }),
      {
        name: "relocation-order-init",
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) =>
          ({
            vehicle: state.vehicle,
            transportMethod: state.transportMethod,
            transportCompanyUuid: state.transportCompanyUuid,
            relocationOrderType: state.relocationOrderType,
            originExternalAddress: state.originExternalAddress,
            destinationBranch: state.destinationBranch,
            destinationExternalAddress: state.destinationExternalAddress,
            earliestPickupDate: serializeEarliestPickupDate(state.earliestPickupDate),
            transportReason: state.transportReason,
          }) satisfies SerializedPersistedRelocationOrderInitState,
        merge: (persistedState, currentState) => {
          const typedPersistedState = (persistedState ?? {}) as Partial<SerializedPersistedRelocationOrderInitState>;

          return {
            ...currentState,
            ...typedPersistedState,
            earliestPickupDate: deserializeEarliestPickupDate(typedPersistedState.earliestPickupDate),
          };
        },
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            console.error("Failed to rehydrate relocation-order-init store", error);
          }

          state?.setHasHydrated(true);
        },
      },
    ),
    { name: "relocation-order-init" },
  ),
);
